// Full-document render of the tempurpedic ProductDisplayPage: PageSkeleton (the
// React document shell) wrapping the content -- the hero (OverlayBanner +
// PDPHero + modular configurator, or the alternative-design gallery/value-prop
// extraction) and the consolidated PDP body -- with the same container/section
// DOM the product-detail SCSS anchors to (#product-detail-container,
// #product-detail, .pdp-hero, .pdp-hero--relative, .pdp-capped-width-content,
// .alternative-hero, .pdp-blocks).
import React from "react";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";

import { Context, templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { GalleryBlockEntry } from "../blocks/toolboxComponentsMap";
import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProductDisplayPageBody } from "../components/ProductDisplayPageBody";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";
import { Template as OverlayBannerAssembly } from "./OverlayBannerAssemblyTemplate";
import { Template as PDPHero } from "./PDPHeroTemplate";

// The alternative design extracts these top-level module block types into the
// hero; the body assembly's `use_default_design=false` filter drops the same
// two types so they are not double-rendered. The mobile duplicate renders
// value-props only.
type PDPModuleBlock = NonNullable<
    templates.ProductDisplayPageTemplate["page"]["modules"]
>["value"][number];
// Narrow to the two hero-extracted block types so `StreamField` only requires a
// component map covering them (both are `buildToolboxPageMap` / base
// `toolboxComponentsMap` keys). The discriminant is optional in the generated
// block types, hence the optional `type` in the Extract constraint.
type AltHeroBlock = Extract<
    PDPModuleBlock,
    { type?: "gallery_block" | "value_prop_bar_block" }
>;
const isAltHeroBlock = (block: PDPModuleBlock): block is AltHeroBlock =>
    block.type === "gallery_block" || block.type === "value_prop_bar_block";
const isAltHeroMobileBlock = (block: PDPModuleBlock): block is AltHeroBlock =>
    block.type === "value_prop_bar_block";

// Mutable reactivated props (not Immutable<>): PageSkeleton composes the in-tree
// SiteHeader chain, which is typed with mutable reactivated props. The hero /
// body templates accept Immutable props, to which these are assignable.
export const Template = (
    props: templates.ProductDisplayPageTemplate,
): React.JSX.Element => {
    const chrome = React.useContext(Context).chrome;
    // The alt-design hero renders the extracted top-level module blocks through
    // the SAME page-aware toolbox map the body uses for those keys (both
    // `gallery_block` and `value_prop_bar_block` are base map entries), so the
    // hero copies of those blocks render identically to how the body renders
    // its other blocks. One override: the alt-hero gallery is the page's MAIN
    // gallery, whose initial index depends on the page state (the `/v/<pk>/`
    // routed variant, then the page `initial_variant` FK) that the serialized
    // `initial_gallery_index` cannot see (computed `page=None`). The server
    // threads the page-aware index per block id; missing entries fall through
    // to `GalleryBlockEntry`'s serialized-index fallback.
    const toolboxMap = {
        ...buildToolboxPageMap(props),
        gallery_block: (block: {
            id?: string;
            value: React.ComponentProps<typeof GalleryBlockEntry>["value"];
        }) => (
            <GalleryBlockEntry
                value={block.value}
                initialGalleryIndex={
                    block.id
                        ? props.alt_hero_gallery_indexes[block.id]
                        : undefined
                }
            />
        ),
    };
    const moduleBlocks: readonly PDPModuleBlock[] =
        props.page.modules?.value ?? [];
    const altHeroBlocks = moduleBlocks.filter(isAltHeroBlock);
    const altHeroMobileBlocks = moduleBlocks.filter(isAltHeroMobileBlock);
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div id="product-detail-container">
                <div id="product-detail">
                    {props.use_default_design ? (
                        <div
                            className={`pdp-hero${
                                props.overlay_banner_relative
                                    ? " pdp-hero--relative"
                                    : ""
                            }`}
                        >
                            <div
                                className={
                                    props.overlay_banner_relative
                                        ? "pdp-hero--relative"
                                        : ""
                                }
                            >
                                <OverlayBannerAssembly
                                    {...props.overlay_banner}
                                />
                                <PDPHero {...props.hero} />
                                <div className="pdp-hero__content l-capped-width">
                                    {/* Always built on the default design;
                                        the null check only narrows the
                                        type. */}
                                    {props.configurator && (
                                        <ModularConfigurator
                                            {...props.configurator}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="pdp-hero pdp-capped-width-content">
                            <div className="alternative-hero">
                                <StreamField
                                    tagName={React.Fragment}
                                    value={altHeroBlocks}
                                    components={toolboxMap}
                                />
                            </div>
                            {props.has_configurator && (
                                <div className="alternative-configurator">
                                    {props.configurator && (
                                        <ModularConfigurator
                                            {...props.configurator}
                                        />
                                    )}
                                </div>
                            )}
                            <div className="alternative-hero__mobile">
                                <StreamField
                                    tagName={React.Fragment}
                                    value={altHeroMobileBlocks}
                                    components={toolboxMap}
                                />
                            </div>
                        </div>
                    )}
                    <div className="pdp-blocks">
                        <ProductDisplayPageBody
                            page={props.page}
                            use_default_design={props.use_default_design}
                            hardcoded_ssr={props.hardcoded_ssr}
                            hotspot_ssr={props.hotspot_ssr}
                            breadcrumbs_ssr={props.breadcrumbs_ssr}
                            customer_service_ssr={props.customer_service_ssr}
                            shop_compare_ssr={props.shop_compare_ssr}
                            tab_feature_attribute_ssr={
                                props.tab_feature_attribute_ssr
                            }
                            reviews_ssr={props.reviews_ssr}
                            reviews={props.reviews}
                            page_num_reviews={props.page_num_reviews}
                            change_variant_ssr={props.change_variant_ssr}
                            change_root_ssr={props.change_root_ssr}
                            root_conditional_ssr={props.root_conditional_ssr}
                            variant_conditional_ssr={
                                props.variant_conditional_ssr
                            }
                            tab_cards_initial_root_product_id={
                                props.tab_cards_initial_root_product_id
                            }
                        />
                    </div>
                </div>
            </div>
            <IovationScripts
                iovationUrl={props.iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
